import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpContext } from '@angular/common/http';
import { DefaultMetricChartsNamesDTO, MetricGroupsAndPeriods } from '../../dto/MetricGroupsAndPeriods';
import { UrlService } from 'src/environments/urlService';
import { MetricGroup } from '../../dto/MetricGroup';
import { IS_SPINNER_ACTIVE } from 'src/app/util/interceptors';
import { map, tap } from 'rxjs/operators';
import { MetricCalculationService } from '../internal/metric-calculation.service';
import { LocalDate } from '../../../../core/pojo/date-variable';
import { MetricData, MetricDataRole } from '../../dto/MetricData';
import { CompanyMetricVisibilityDto, MetricGroupCompanyOrderDTO } from '../../dto/CompanyMetric';
import { UUID } from '../../../../core/pojo/id-variable';
import {
  BulkRequestFetchPeriodsResponse,
  MetricBulkRequestCompanyWrapper,
  MetricBulkRequestDto,
  MetricBulkRequestTestEmailDTO,
} from "../../dto/metric-bulk-request";
import { MetricDeleteResponseDto, MetricGroupDeleteResponseDto } from "../../dto/Metric";

@Injectable({
  providedIn: 'root',
})
export class KpiBusinessService {
  private readonly url: string;

  constructor(
    private http: HttpClient,
    private urlService: UrlService,
  ) {
    this.url = this.urlService.getFmsUrl() + 'kpi';
  }

  updateMetricData(metricData: MetricData[], companyUUID: string): Observable<void> {
    return this.http.put<void>(
      this.url + '/metrics/data', metricData,
      {
        params: { companyUUID },
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
        withCredentials: true,
      },
    );
  }

  addPeriods(periods: LocalDate[], companyUUID: string, role: MetricDataRole): Observable<void> {
    return this.http.put<void>(
      this.url + '/metrics/periods', periods,
      {
        params: { companyUUID, role },
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
        withCredentials: true,
      },
    );
  }

  deletePeriods(periods: LocalDate[], companyUUID: string, role: MetricDataRole): Observable<void> {
    return this.http.delete<void>(
      this.url + '/metrics/periods',
      {
        params: { companyUUID, role, periods },
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
        withCredentials: true,
      },
    );
  }

  bulkDeletePeriods(periods: LocalDate[], orgUUID: string, role: MetricDataRole): Observable<void> {
    return this.http.delete<void>(
      this.url + '/metrics/periods-bulk',
      {
        params: { orgUUID, role, periods },
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
        withCredentials: true,
      },
    );
  }

  updateMetricGroupsVisibility(dtos: CompanyMetricVisibilityDto[], companyUUID: string): Observable<void> {
    return this.http.put<void>(
      this.url + '/metrics/groups/visibility', dtos,
      {
        params: { companyUUID },
        withCredentials: true,
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      },
    );
  }

  updateMetricGroupsOrder(dtos: MetricGroupCompanyOrderDTO[], companyUUID: string): Observable<void> {
    return this.http.put<void>(
      this.url + '/metrics/groups/order', dtos,
      {
        params: { companyUUID },
        withCredentials: true,
        context: new HttpContext().set(IS_SPINNER_ACTIVE, false),
      },
    );
  }

  saveAndGetMetricGroup(group: MetricGroup, orgUUID: string, companyUuid: string, isEnabledForAllCompanies: boolean): Observable<MetricGroup> {
    return this.http.post<MetricGroup>(
      this.url + '/metrics/groups-with-return', group,
      {
        params: { orgUUID, companyUuid, isEnabledForAllCompanies },
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
        withCredentials: true,
      },
    ).pipe(
      tap(MetricCalculationService.setGroupVisibility),
      tap(MetricCalculationService.setSignsForGroup)
    );
  }

  updateMetricGroup(group: MetricGroup, orgUUID: string): Observable<MetricGroup> {
    return this.http.put<MetricGroup>(
      this.url + '/metrics/groups', group,
      {
        params: { orgUUID },
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
        withCredentials: true,
      },
    );
  }

  getCompaniesWithMetricData(groupUUID: string): Observable<MetricGroupDeleteResponseDto> {
    return this.http.get<MetricGroupDeleteResponseDto>(this.url + '/metrics/groups/companies-with-data',
      {
        params: { groupUUID },
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
        withCredentials: true,
      });
  }

  deleteGroup(groupUUID: string): Observable<void> {
    return this.http.delete<void>(
      this.url + '/metrics/groups',
      {
        params: { groupUUID },
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
        withCredentials: true,
      },
    );
  }

  getMetricsNamesForCharts(): Observable<DefaultMetricChartsNamesDTO> {
    return this.http.get<DefaultMetricChartsNamesDTO>(
      this.url + '/metrics/charts',
      {
        withCredentials: true,
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      },
    );
  }

  getMetricGroups(orgUUID: UUID): Observable<MetricGroup[]> {
    return this.http.get<MetricGroup[]>(
      this.url + '/metrics/org',
      {
        params: { orgUUID },
        withCredentials: true,
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      },
    );
  }

  getBatchAmountOfCompaniesWithData(orgUUID: UUID, metricsUUID: UUID[]): Observable<Record<UUID, number>> {
    return this.http.get<Record<UUID, number>>(
      this.url + '/metrics/org/companies-amount/batch',
      {
        params: { orgUUID, metricsUUID },
        withCredentials: true,
        context: new HttpContext().set(IS_SPINNER_ACTIVE, false),
      },
    );
  }


  getFullMetricGroups(orgUUID: UUID): Observable<MetricGroup[]> {
    return this.http.get<MetricGroup[]>(
      this.url + '/metrics/org/full',
      {
        params: { orgUUID },
        withCredentials: true,
        context: new HttpContext().set(IS_SPINNER_ACTIVE, false),
      },
    ).pipe(
      map((groups) => {
        const deserializedGroups = groups.map((group) => MetricGroup.deserializeMetricGroup(group));
        MetricCalculationService.setSignsForGroups(deserializedGroups);
        MetricCalculationService.setGroupsVisibility(deserializedGroups);
        return groups;
      }));
  }

  getIsMetricDeletable(metricUUID: string): Observable<MetricDeleteResponseDto> {
    return this.http.get<MetricDeleteResponseDto>(this.url + '/metrics/metric/deletable',
      {
        params: { metricUUID },
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
        withCredentials: true,
      });
  }

  /**
   * Retrieves the Metric Groups and Periods.
   * Afterward deserializes the objects in order to use the internal object functions (We receive any instance).
   * Sets the temporary frontend properties, such as icons for the groups.
   *
   * @param companyUUID - The unique identifier of the company.
   * @return - An observable that emits the MetricGroupsAndPeriods data.
   */
  get(companyUUID: string): Observable<MetricGroupsAndPeriods> {
    return this.http.get<MetricGroupsAndPeriods>(
      this.url + '/metrics',
      {
        params: { companyUUID },
        withCredentials: true,
      },
    ).pipe(
      map((groupAndPeriods) => {
        const deserializedResponse = MetricGroupsAndPeriods.deserializeMetricGroupsAndPeriods(groupAndPeriods);
        MetricCalculationService.setSignsForGroups(deserializedResponse.metricGroups);
        MetricCalculationService.setGroupsVisibility(deserializedResponse.metricGroups);
        return deserializedResponse;
      }));
  }

  getBulkRequestCompanyWrappers(orgUUID: UUID, periods: LocalDate[], allPeriods: LocalDate[]): Observable<MetricBulkRequestCompanyWrapper[]> {
    return this.http.get<MetricBulkRequestCompanyWrapper[]>(
      this.url + '/org/bulk-request-wrappers',
      {
        params: { orgUUID, periods },
        withCredentials: true,
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      },
    ).pipe(
      map((wrappers) => {
        return wrappers.map((wrapper) => {
          return MetricBulkRequestCompanyWrapper.deserialize(wrapper, periods, allPeriods);
        });
      })
    );
  }

  fetchPeriods(orgUUID: UUID, periods: LocalDate[]): Observable<BulkRequestFetchPeriodsResponse[]> {
    return this.http.get<BulkRequestFetchPeriodsResponse[]>(
      this.url + '/org/fetch-periods',
      {
        params: { orgUUID, periods },
        withCredentials: true,
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      },
    );
  }

  getUniquePeriods(orgUUID: UUID): Observable<LocalDate[]> {
    return this.http.get<LocalDate[]>(
      this.url + '/org/unique-periods',
      {
        params: { orgUUID },
        withCredentials: true,
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      },
    );
  }

  kpiBulkRequest(bulkRequest: MetricBulkRequestDto, orgUUID: UUID): Observable<void> {
    return this.http.post<void>(this.url + '/org/bulk-request-wrappers',
      bulkRequest,
      {
        params: { orgUUID },
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
        withCredentials: true,
      });
  }

  kpiBulkRequestTestEmail(bulkRequest: MetricBulkRequestTestEmailDTO, orgUUID: UUID): Observable<void> {
    return this.http.post<void>(this.url + '/org/bulk-request-wrappers/test-email',
      bulkRequest,
      {
        params: { orgUUID },
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
        withCredentials: true,
      });
  }

  getMetricGroupsForCompany(orgUUID: string, companyUuid: string): Observable<MetricGroup[]> {
    return this.http.get<MetricGroup[]>(
      this.url + '/metrics/groups',
      {
        params: { orgUUID, companyUuid },
        withCredentials: true,
        context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      },
    ).pipe(
      map((groups) => {
        return groups.map((group) => MetricGroup.deserializeMetricGroup(group));
      }),
    );
  }
}
